import { getBarScoreColor } from "../ui/lib/getScoreColor";

export type TopicKey = 'verbal' | 'data' | 'quant';

export interface Subskill {
  title: string;
  score: number;
  category: string;
}

export interface SkillItem {
  title: string;
  description: string;
  score: number;
}

export interface SkillGroup {
  title: string;
  items: SkillItem[];
}

export interface PerformanceConfig {
  accuracy: number;
  accuracyColor: string;
  progress: number[];
  progressColor: string;
}

export interface PracticeTopicConfig {
  title: string;
  subtitle: string;
  description: string;
  audioGuide: string;
  audioDuration: string;
  subskills: Subskill[];
  groups: SkillGroup[];
  performance: PerformanceConfig;
}

export const PRACTICE_TOPICS: Record<TopicKey, PracticeTopicConfig> = {
  quant: {
    title: 'Quantitative Reasoning',
    subtitle: 'GMAT Prep',
    description:
      'Test your ability to solve math problems, analyze data, and apply logic. Focused on problem-solving skills, not memorizing formulas.',
    audioGuide: 'Audio Guide: Quant Section',
    audioDuration: '13:27',
    subskills: [
      { title: 'Probability', score: 49, category: 'Arithmetic' },
      { title: 'Algebraic expressions and polynomials', score: 66, category: 'Algebra' },
      { title: 'Decimals', score: 73, category: 'Arithmetic' },
    ],
    groups: [
      {
        title: 'Algebra',
        items: [
          {
            title: 'Algebraic expressions and polynomials',
            description:
              'Manipulate and simplify algebraic terms, factorize, and solve for unknowns in polynomial equations.',
            score: 66,
          },
          {
            title: 'Functions',
            description: 'Work with function notation, composition, and interpretation of inputs/outputs.',
            score: 88,
          },
          {
            title: 'Inequalities',
            description: 'Solve linear and quadratic inequalities and understand their solution sets.',
            score: 87,
          },
          {
            title: 'Linear equations',
            description: 'Formulate and solve single-variable and system equations.',
            score: 79,
          },
          {
            title: 'Permutations and combinations',
            description: 'Count outcomes using fundamental counting rules and combinatorics.',
            score: 93,
          },
          {
            title: 'Progressions',
            description: 'Work with arithmetic and geometric sequences and their sums.',
            score: 99,
          },
          {
            title: 'Quadratic equations',
            description: 'Solve quadratic equations and analyze roots.',
            score: 100,
          },
        ],
      },
      {
        title: 'Arithmetic',
        items: [
          {
            title: 'Averages',
            description: 'Compute mean, median, and weighted averages for data sets.',
            score: 89,
          },
          {
            title: 'Decimals',
            description: 'Work with decimals, rounding, and precision in calculations.',
            score: 73,
          },
          {
            title: 'Fractions',
            description: 'Simplify and operate on fractions and ratios.',
            score: 93,
          },
          {
            title: 'Mixtures and allegations',
            description: 'Solve mixture problems and composition percentages.',
            score: 91,
          },
          {
            title: 'Number properties',
            description: 'Apply divisibility rules, primes, and factors.',
            score: 78,
          },
          {
            title: 'Percentages',
            description: 'Solve percent change and comparison problems.',
            score: 90,
          },
          {
            title: 'Powers and roots',
            description: 'Manipulate exponents, radicals, and roots.',
            score: 85,
          },
          {
            title: 'Probability',
            description: 'Compute probabilities for independent and dependent events.',
            score: 49,
          },
        ],
      },
    ],
    performance: {
      accuracy: 91,
      accuracyColor: getBarScoreColor(91),
      progress: [62, 64, 63, 66, 68, 70, 72, 69, 73, 76, 74, 78, 80, 79, 82, 85, 83, 87, 88, 91],
      progressColor: getBarScoreColor(91),
    },
  },
  verbal: {
    title: 'Verbal Reasoning',
    subtitle: 'GMAT Prep',
    description:
      'Assess your reading comprehension, critical reasoning, and sentence correction skills. Master the art of analyzing arguments and understanding complex passages.',
    audioGuide: 'Audio Guide: Verbal Section',
    audioDuration: '12:45',
    subskills: [
      { title: 'Critical reasoning', score: 58, category: 'Reasoning' },
      { title: 'Reading comprehension', score: 64, category: 'Comprehension' },
      { title: 'Sentence correction', score: 71, category: 'Grammar' },
    ],
    groups: [
      {
        title: 'Critical Reasoning',
        items: [
          {
            title: 'Argument evaluation',
            description: 'Identify premises, conclusions, and assumptions in arguments.',
            score: 58,
          },
          {
            title: 'Strengthen/Weaken',
            description: 'Determine evidence that strengthens or weakens an argument.',
            score: 65,
          },
          {
            title: 'Inference',
            description: 'Draw logically supported conclusions from given statements.',
            score: 72,
          },
          {
            title: 'Boldface questions',
            description: 'Analyze the roles of boldfaced statements in arguments.',
            score: 68,
          },
        ],
      },
      {
        title: 'Reading & Language',
        items: [
          {
            title: 'Reading comprehension',
            description: 'Understand main ideas, details, and author tone.',
            score: 64,
          },
          {
            title: 'Sentence correction',
            description: 'Fix errors in grammar, parallelism, and modifiers.',
            score: 71,
          },
          {
            title: 'Meaning and style',
            description: 'Ensure clarity and concision while preserving meaning.',
            score: 77,
          },
          {
            title: 'Logical predication',
            description: 'Recognize misplaced modifiers and faulty comparisons.',
            score: 69,
          },
        ],
      },
    ],
    performance: {
      accuracy: 65,
      accuracyColor: getBarScoreColor(65),
      progress: [54, 56, 58, 57, 60, 62, 61, 64, 66, 65, 68, 70, 69, 71, 73, 75, 76].map((x)=> x-11),
      progressColor: getBarScoreColor(65),
    },
  },
  data: {
    title: 'Data Insights',
    subtitle: 'GMAT Prep',
    description:
      'Evaluate your ability to analyze data from multiple sources, interpret graphics, and make data-driven decisions. Essential for modern business analytics.',
    audioGuide: 'Audio Guide: Data Section',
    audioDuration: '14:30',
    subskills: [
      { title: 'Data sufficiency', score: 61, category: 'Logic' },
      { title: 'Table analysis', score: 68, category: 'Analysis' },
      { title: 'Multi-source reasoning', score: 72, category: 'Synthesis' },
    ],
    groups: [
      {
        title: 'Data Interpretation',
        items: [
          {
            title: 'Table analysis',
            description: 'Extract insights from complex tables and datasets.',
            score: 68,
          },
          {
            title: 'Graphics interpretation',
            description: 'Analyze charts, graphs, and visualized data.',
            score: 75,
          },
          {
            title: 'Rates and ratios',
            description: 'Apply proportional reasoning to data scenarios.',
            score: 81,
          },
        ],
      },
      {
        title: 'Decision & Logic',
        items: [
          {
            title: 'Data sufficiency',
            description: 'Judge whether provided data is sufficient to solve problems.',
            score: 61,
          },
          {
            title: 'Multi-source reasoning',
            description: 'Synthesize information from multiple sources.',
            score: 72,
          },
          {
            title: 'Decision trees',
            description: 'Map outcomes and evaluate conditional paths.',
            score: 79,
          },
        ],
      },
    ],
    performance: {
      accuracy: 49,
      accuracyColor: getBarScoreColor(49),
      progress: [48, 50, 52, 51, 55, 54, 58, 60, 59, 61, 63, 62, 65, 66, 67, 49],
      progressColor:  getBarScoreColor(49),
    },
  },
};

export const DEFAULT_PRACTICE_TOPIC: TopicKey = 'quant';

export const getPracticeTopic = (topic?: TopicKey) => {
  const resolvedTopic = topic ?? DEFAULT_PRACTICE_TOPIC;
  return {
    topic: resolvedTopic,
    config: PRACTICE_TOPICS[resolvedTopic] ?? PRACTICE_TOPICS[DEFAULT_PRACTICE_TOPIC],
  };
};
